import { useState, useEffect } from 'react';

// Build a quick lookup: team_id -> { thumb, full } from a teams array.
export function buildLogoMap(teams) {
  const map = {};
  (teams || []).forEach(t => {
    if (t.logo_url || t.logo_full_url) {
      map[t.id] = { thumb: t.logo_url || t.logo_full_url, full: t.logo_full_url || t.logo_url };
    }
  });
  return map;
}

// Group bracket matches by division.
// Returns [{ divId, divName, matches }] — single "All" group when no divisions.
export function groupByDivision(matches, divisions) {
  const divs = divisions || [];
  if (!divs.length) return [{ divId: null, divName: null, matches: matches || [] }];
  const groups = divs.map(d => ({
    divId: d.id, divName: d.name,
    matches: (matches || []).filter(m => m.division_id === d.id),
  })).filter(g => g.matches.length > 0);
  const orphan = (matches || []).filter(m => !m.division_id || !divs.find(d => d.id === m.division_id));
  if (orphan.length) groups.push({ divId: null, divName: null, matches: orphan });
  return groups;
}

function initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/[\s/]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// Crisp rounded team logo with an initials fallback. Click → onClick(fullUrl).
export function TeamLogo({ logo, name, size = 28, onClick, ring = '#2A2F37' }) {
  const has = logo && logo.thumb;
  const base = {
    width: size, height: size, borderRadius: 6, flexShrink: 0,
    border: `1px solid ${ring}`, objectFit: 'cover', background: '#0A0A0A',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: Math.max(9, Math.round(size * 0.38)), fontWeight: 800, color: '#94A3B8',
    cursor: has && onClick ? 'zoom-in' : 'default', overflow: 'hidden',
  };
  if (!has) {
    return <span aria-hidden style={base} data-testid="team-logo-placeholder">{initials(name)}</span>;
  }
  return (
    <img
      src={logo.thumb} alt={name || 'logo'} loading="lazy"
      data-testid="team-logo"
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(logo.full || logo.thumb); } : undefined}
      style={base}
    />
  );
}

// Fullscreen full-resolution logo viewer.
export function LogoLightbox({ url, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!url) return null;
  return (
    <div data-testid="logo-lightbox" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}>
      <img src={url} alt="Team logo" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, objectFit: 'contain' }} />
    </div>
  );
}

// Convenience hook to manage a single lightbox URL.
export function useLogoLightbox() {
  const [url, setUrl] = useState(null);
  return { lightboxUrl: url, openLightbox: setUrl, closeLightbox: () => setUrl(null) };
}
